import React, {useState} from 'react';

import {isEditableAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {isAnalysisTemplateGraphQL} from 'analysis-tool/AnalysisTypes';
import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {getPossibleEdges} from 'pipelines/StepConnectionUtils';
import {MenuPanelSection} from 'ui-library/menus/MenuPanel';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';
import StepTypeSelector, {canAddStepType} from './StepTypeSelector';

/**
 * True if the step type can be connected to `step` and `stepData`.
 */
function canConnectStepTypeToStepData(
  step: PipelineStep,
  stepData: PipelineStepData,
  newStepType: PipelineStepType
): boolean {
  const possibleEdges = step.inputStepDatas.find(({id}) => id === stepData.id)
    ? getPossibleEdges({
        fromStepType: newStepType,
        toStepType: step.stepType,
        toStepData: {systemName: stepData.name},
      })
    : getPossibleEdges({
        toStepType: newStepType,
        fromStepType: step.stepType,
        fromStepData: {systemName: stepData.name},
      });

  // There may be more than one possible connection to the next step, such as to either the `Left` or `Right`
  // input of a join. In this scenario, the `addStep` util will choose a valid edge, and the user can
  // drag-and-drop edges to fix any incorrect connections.
  return possibleEdges.length > 0;
}

const ADD_SOURCE_STEP_EXTRA_SECTIONS: Array<MenuPanelSection> = [
  {
    header: 'Use existing tables',
    rows: [
      {
        id: PipelineStepType.JOIN,
        label: 'Join or union tables',
        description: 'Create a table by combining existing tables in your analysis',
      },
      {
        id: PipelineStepType.LOOK_UP,
        label: 'Look up table',
        description: 'Create a table by looking up data from an existing table in your analysis',
      },
    ],
  },
  {
    header: 'External data',
    rows: [
      {
        id: PipelineStepType.THIRD_PARTY,
        label: 'Connect with external tool',
        description: 'Send or receive data from an external analytics tool',
      },
    ],
  },
];

/**
 * Add a new step to the pipeline.
 *
 * If `selectedRightSidebarItem.stepId` and `selectedRightSidebarItem.stepDataName` are set, the new step
 * will be connected to the step data.
 *
 * Only step types can be edited from `AnalysisFlowchart` can be added.
 *
 */
export default function AddStepRightSidebar({
  analysis: pipeline,
  onChangeSelectedRightSidebarItem,
  selectedRightSidebarItem,
}: RightSidebarComponentProps<
  AnalysisRightSidebarItemType.ADD_STEP | AnalysisRightSidebarItemType.ADD_SOURCE_STEP
>): JSX.Element {
  const {stepId, stepDataName} =
    selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_STEP
      ? selectedRightSidebarItem
      : {stepId: null, stepDataName: null};
  const [selectedStepType, setSelectedStepType] = useState<PipelineStepType | null>(null);

  const step = stepId ? getStepFromAnalysis(pipeline, stepId) : null;
  const stepData = step
    ? [...step.inputStepDatas, ...step.outputStepDatas].find(({name}) => name === stepDataName)
    : undefined;

  const extraSections: Array<MenuPanelSection> | undefined =
    selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP
      ? ADD_SOURCE_STEP_EXTRA_SECTIONS
      : undefined;

  // Add the new step type when Next is clicked
  const onNext = async (): Promise<void> => {
    if (!selectedStepType) {
      return;
    }
    if (selectedStepType === PipelineStepType.THIRD_PARTY) {
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.THIRD_PARTY_IMPORT,
      });
      return;
    }
    if (selectedStepType === PipelineStepType.JOIN) {
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.COMBINE_TABLES_SOURCE,
        initialCombineMethod: PipelineStepType.JOIN,
      });
      return;
    }
    if (selectedStepType === PipelineStepType.UNION_DATASETS) {
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.COMBINE_TABLES_SOURCE,
        initialCombineMethod: PipelineStepType.UNION_DATASETS,
      });
      return;
    }
    if (
      selectedStepType === PipelineStepType.LOOK_UP &&
      selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP
    ) {
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.LOOK_UP_SOURCE,
      });
      return;
    }
    // When editing a template, creating a table from a dataset produces a TEMPLATE_INPUT step rather than a
    // normal dataset step, so route to a dedicated panel that collects the dataset and variable/static choice.
    if (
      selectedStepType === PipelineStepType.DATA_CATALOG_DATASET &&
      selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP &&
      isAnalysisTemplateGraphQL(pipeline) &&
      isEditableAnalysisTemplatesEnabled()
    ) {
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE,
      });
      return;
    }
    const {stepId} = await withLoadingScreen(
      addStepWithInferredEdges(pipeline, {stepType: selectedStepType, stepData})
    );
    await withLoadingScreen(refreshAnalysis(pipeline.id));
    onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
  };

  const title =
    selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP
      ? 'Add source step'
      : 'Add step';

  /**
   * For ADD_STEP mode, only allow step types which can be connected to `step` and `stepData`.
   * For ADD_SOURCE_STEP mode, the default source step type filter from StepTypeSelector is used.
   */
  const filterStepTypes =
    step && stepData
      ? (stepType: PipelineStepType): boolean =>
          canAddStepType(stepType) && canConnectStepTypeToStepData(step, stepData, stepType)
      : undefined;

  return (
    <RightSidebarSkeleton
      title={title}
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !selectedStepType,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddStepRightSidebar',
        element: 'root',
      }}
    >
      <StepTypeSelector
        selectedStepType={selectedStepType}
        onSelectStepType={setSelectedStepType}
        extraSections={extraSections}
        filterStepTypes={filterStepTypes}
      />
    </RightSidebarSkeleton>
  );
}
